import {
  Customers_Profile_PublicContactPreferencesMutationNLRequestModel,
  DC_Domain_Models_CustomerRatings_BusinessType,
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  RequestDataDC_Customers_Client_Models_CustomerDiscontinueConfirmRequestModel,
  RequestDataDC_Customers_Client_Models_CustomerDiscontinueIntakeRequestModel,
  RequestDataSystem_String,
  RequestModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchRequestModel,
  RequestModels_Customers_SwitchType_SwitchTypeRequestModel,
  ResponseDataDC_Belgium_Client_Models_AmountActiveUsers,
  ResponseDataDC_Customers_Client_Models_CustomerDiscontinueIntakeResponseModel,
  ResponseDataDC_Domain_Models_Products_OrdersV2_OrderType,
  ResponseDataSystem_Guid,
  ResponseModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel,
  ResponseModels_Customers_CustomerEvents_CustomerEventsResponseModel,
  ResponseModels_Customers_CustomerEvents_MonthlyInstallmentEventsResponseModel,
  ResponseModels_Customers_CustomerRatings_AverageReviewScoreResponseModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type GetSwitchType = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  requestBody?: RequestModels_Customers_SwitchType_SwitchTypeRequestModel;
};
/**
 * Returns a OrderType: INH, WSW,PSW, CIPS
 *
 * @returns ResponseDataDC_Domain_Models_Products_OrdersV2_OrderType Success
 */
export function getSwitchType(
  { businessUnit, label, requestBody }: GetSwitchType,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseDataDC_Domain_Models_Products_OrdersV2_OrderType> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/customers/switchtype`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type UnsubscribeFromContactPreference = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  recipient: string;
  requestBody?: Customers_Profile_PublicContactPreferencesMutationNLRequestModel;
};
/**
 * Update customer preference
 *
 * @returns void
 */
export function unsubscribeFromContactPreference(
  { businessUnit, label, recipient, requestBody }: UnsubscribeFromContactPreference,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'PATCH',
      path: `/dxpweb/public/${businessUnit}/${label}/preferences/contact`,
      query: { recipient },
      body: requestBody,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetCustomerRatings = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  businessType?: DC_Domain_Models_CustomerRatings_BusinessType;
};
/**
 * Get average customer rating score from Expoints
 *
 * @returns ResponseModels_Customers_CustomerRatings_AverageReviewScoreResponseModel Success
 */
export function getCustomerRatings(
  { businessUnit, label, businessType }: GetCustomerRatings,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Customers_CustomerRatings_AverageReviewScoreResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/public/${businessUnit}/${label}/averagereviewscore`,
      query: { businessType },
      errors: { 404: 'Not Found' },
    },
    requestConfig,
  );
}

type ChamberOfCommerceSearch = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  requestBody?: RequestModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchRequestModel;
};
/**
 * Requests basic business information.
 *
 * @returns ResponseModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel Success
 */
export function chamberOfCommerceSearch(
  { businessUnit, label, requestBody }: ChamberOfCommerceSearch,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Customers_ChamberOfCommerce_ChamberOfCommerceSearchResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/customers/chamberofcommerce/search`,
      body: requestBody,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type PostCustomerOrderConfirmation = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  orderId: string;
};
/**
 * Customers receive an email after placing an order with a confirmation button. Pressing this button sends a confirmation towards CRM
 *
 * @returns any Success
 */
export function postCustomerOrderConfirmation(
  { businessUnit, label, orderId }: PostCustomerOrderConfirmation,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/orders/${orderId}/confirmation`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type PostCustomerDiscontinueIntake = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: RequestDataDC_Customers_Client_Models_CustomerDiscontinueIntakeRequestModel;
};
/**
 * Post customer discontinue intake
 *
 * @returns ResponseDataDC_Customers_Client_Models_CustomerDiscontinueIntakeResponseModel Success
 */
export function postCustomerDiscontinueIntake(
  { businessUnit, label, customerId, requestBody }: PostCustomerDiscontinueIntake,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseDataDC_Customers_Client_Models_CustomerDiscontinueIntakeResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/customers/${customerId}/discontinue/intake`,
      body: requestBody,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type PostCustomerDiscontinueConfirm = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: RequestDataDC_Customers_Client_Models_CustomerDiscontinueConfirmRequestModel;
};
/**
 * Posts the customer discontinue confirm (Customer is deceased).
 *
 * @returns ResponseDataSystem_Guid Success
 */
export function postCustomerDiscontinueConfirm(
  { businessUnit, label, customerId, requestBody }: PostCustomerDiscontinueConfirm,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseDataSystem_Guid> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/customers/${customerId}/discontinue/confirm`,
      body: requestBody,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetActiveUsers = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
};
/**
 * Get the amount of active users
 *
 * @returns ResponseDataDC_Belgium_Client_Models_AmountActiveUsers Success
 */
export function getActiveUsers(
  { businessUnit, label }: GetActiveUsers,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseDataDC_Belgium_Client_Models_AmountActiveUsers> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/public/${businessUnit}/${label}/customers/active-users`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type PostCustomerCollectionStepEventsForAdministrator = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  requestBody: RequestDataSystem_String;
};
/**
 * Get CustomerCollectionStepEvents based on the posted bewindvoerder email
 *
 * @returns ResponseModels_Customers_CustomerEvents_CustomerEventsResponseModel Success
 */
export function postCustomerCollectionStepEventsForAdministrator(
  { businessUnit, label, requestBody }: PostCustomerCollectionStepEventsForAdministrator,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Customers_CustomerEvents_CustomerEventsResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/customers/collectionstepevents`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type PostCustomerMonthlyInstallmentEventsForAdministrator = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  requestBody: RequestDataSystem_String;
};
/**
 * Get monthlyInstallmentEvents based on the posted bewindvoerder email
 *
 * @returns ResponseModels_Customers_CustomerEvents_MonthlyInstallmentEventsResponseModel Success
 */
export function postCustomerMonthlyInstallmentEventsForAdministrator(
  { businessUnit, label, requestBody }: PostCustomerMonthlyInstallmentEventsForAdministrator,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Customers_CustomerEvents_MonthlyInstallmentEventsResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/public/${businessUnit}/${label}/customers/monthlyinstallmentevents`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}
